/**
 * VerdictChangesStrip -- "Morning briefing" strip showing overnight verdict changes.
 *
 * Renders between VerdictCards and ProjectComparison on the Verdicts tab.
 * Shows creative name, from/to verdict badges with color-coded arrows, rule label.
 * Dismissable via localStorage; re-appears when newer changes arrive.
 *
 * Phase 29: Verdict Lifecycle & Time-Series Integration.
 */

import { Bell, X, ArrowRight } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VerdictChange {
  creativeId: string;
  creativeName: string;
  from: 'Scale' | 'Fix' | 'Kill';
  to: 'Scale' | 'Fix' | 'Kill';
  ruleLabel: string;
  timestamp: string;
}

interface VerdictChangesStripProps {
  changes?: VerdictChange[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LS_KEY = 'adverdict_changes_strip_dismissed';

const VERDICT_RANK: Record<string, number> = { Scale: 3, Fix: 2, Kill: 1 };

const VERDICT_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  Scale: {
    text: '#22C55E',
    bg: 'rgba(35, 193, 107, 0.12)',
    border: 'rgba(35, 193, 107, 0.25)',
  },
  Fix: {
    text: '#F59E0B',
    bg: 'rgba(255, 176, 32, 0.14)',
    border: 'rgba(255, 176, 32, 0.30)',
  },
  Kill: {
    text: '#EF4444',
    bg: 'rgba(255, 77, 79, 0.12)',
    border: 'rgba(255, 77, 79, 0.25)',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isUpgrade(from: string, to: string): boolean {
  return (VERDICT_RANK[to] ?? 0) > (VERDICT_RANK[from] ?? 0);
}

function getArrowColor(from: string, to: string): string {
  return isUpgrade(from, to) ? '#22C55E' : '#EF4444';
}

function getMaxTimestamp(changes: VerdictChange[]): number {
  return Math.max(...changes.map((c) => new Date(c.timestamp).getTime()));
}

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const date = new Date(isoString);
  return `Today, ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function VerdictBadge({ verdict }: { verdict: string }) {
  const colors = VERDICT_COLORS[verdict] ?? VERDICT_COLORS.Fix!;
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
      style={{
        color: colors.text,
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      {verdict}
    </span>
  );
}

function ChangeRow({ change }: { change: VerdictChange }) {
  const arrowColor = getArrowColor(change.from, change.to);
  const direction = isUpgrade(change.from, change.to) ? 'upgraded' : 'downgraded';
  const srText = `${change.creativeName} ${direction} from ${change.from} to ${change.to}, matched rule: ${change.ruleLabel}`;

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-surface border border-border-subtle
      max-md:flex-wrap max-md:gap-1.5">
      {/* Screen reader description */}
      <span className="sr-only">{srText}</span>

      {/* Creative name */}
      <span className="text-sm font-medium text-primary truncate max-w-[200px] max-lg:max-w-[150px] max-md:max-w-full max-md:w-full">
        {change.creativeName}
      </span>

      {/* Arrow section */}
      <div className="flex items-center gap-1.5">
        <VerdictBadge verdict={change.from} />
        <ArrowRight
          className="w-3.5 h-3.5 flex-shrink-0"
          style={{ color: arrowColor }}
          aria-hidden="true"
        />
        <VerdictBadge verdict={change.to} />
      </div>

      {/* Rule label */}
      <span className="text-xs text-muted truncate max-w-[180px] max-md:max-w-full max-md:w-full">
        &mdash; {change.ruleLabel}
      </span>

      {/* Timestamp (hidden on tablet) */}
      <span className="text-xs text-muted ml-auto whitespace-nowrap max-lg:hidden">
        {formatRelativeTime(change.timestamp)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function VerdictChangesStrip({ changes }: VerdictChangesStripProps) {
  const [dismissed, setDismissed] = useState(false);

  // Compute max timestamp from changes
  const maxTs = useMemo(() => {
    if (!changes || changes.length === 0) return 0;
    return getMaxTimestamp(changes);
  }, [changes]);

  // Check localStorage on mount
  useEffect(() => {
    if (maxTs === 0) return;
    const stored = localStorage.getItem(LS_KEY);
    if (stored) {
      const storedTs = new Date(stored).getTime();
      if (storedTs >= maxTs) {
        setDismissed(true);
      } else {
        // New changes arrived -- re-show
        setDismissed(false);
      }
    }
  }, [maxTs]);

  // Render nothing if no changes or dismissed
  if (!changes || changes.length === 0 || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    const maxIso = new Date(maxTs).toISOString();
    localStorage.setItem(LS_KEY, maxIso);
    setDismissed(true);
  };

  return (
    <div
      className="bg-surface-subtle rounded-xl border border-border-subtle p-4 mt-3"
      role="region"
      aria-label="Overnight verdict changes"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted" />
          <span className="text-xs font-semibold uppercase tracking-widest text-muted">
            Overnight Changes
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-surface transition-colors"
          aria-label="Dismiss overnight changes"
        >
          <X className="w-4 h-4 text-muted hover:text-primary" />
        </button>
      </div>

      {/* Change rows */}
      <div className="space-y-1.5">
        {changes.map((change) => (
          <ChangeRow key={`${change.creativeId}-${change.timestamp}`} change={change} />
        ))}
      </div>
    </div>
  );
}

export default VerdictChangesStrip;
